// CB:BEGIN fr-014-realtor-quote-detail
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';
import { ArrowLeft, FileText, Link2 } from 'lucide-react';

const RealtorQuoteDetail: React.FC<{ quoteId: string; onNavigate: (screen: string) => void }> = ({ quoteId, onNavigate }) => {
  const { getToken } = useAuth();
  const [request, setRequest] = useState<{
    id: string;
    propertyAddressLine1: string;
    status: string;
    bids: Array<{ id: string; amountCents?: number; scopeNotes?: string; contractor?: { company?: string } }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const token = await getToken();
        const base = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${base}/api/realtor/quotes/${quoteId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setRequest(data.request);
        }
      } catch (e) {
        console.error('Fetch quote detail failed', e);
      } finally {
        setLoading(false);
      }
    };
    if (quoteId) fetchDetail();
  }, [quoteId, getToken]);

  const handleDownloadPdf = async () => {
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/quotes/${quoteId}/pdf`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (res.ok) {
        const data = await res.json();
        if (data.downloadUrl) window.open(data.downloadUrl, '_blank');
        else setPdfUrl(data.downloadUrl || 'Generated');
      }
    } catch (e) {
      console.error('PDF failed', e);
    }
  };

  const handleCreatePortalInvite = async () => {
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/quotes/${quoteId}/portal-invite`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (res.ok) {
        const data = await res.json();
        setPortalUrl(data.portalUrl || '');
        if (data.portalUrl) {
          await navigator.clipboard.writeText(data.portalUrl);
          alert('Portal link copied to clipboard.');
        }
      }
    } catch (e) {
      console.error('Portal invite failed', e);
    }
  };

  if (loading || !request) return <div className="p-8">Loading...</div>;
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={() => onNavigate('realtor-dashboard')} className="p-2 rounded hover:bg-gray-100"><ArrowLeft size={20} /></button>
          <h1 className="font-semibold text-lg">Quote: {request.propertyAddressLine1}</h1>
        </div>
      </header>
      <main className="max-w-4xl mx-auto py-8 px-6">
        <p className="text-gray-600 mb-6">Status: {request.status}</p>
        <div className="flex gap-4 mb-8">
          <button onClick={handleDownloadPdf} className="px-4 py-2 bg-gray-800 text-white rounded-lg font-medium flex items-center gap-2">
            <FileText size={18} /> Download PDF
          </button>
          <button onClick={handleCreatePortalInvite} className="px-4 py-2 bg-emerald-700 text-white rounded-lg font-medium flex items-center gap-2">
            <Link2 size={18} /> Invite Client (Portal Link)
          </button>
        </div>
        {portalUrl && <p className="text-sm text-gray-600 mb-4">Portal link (copied): {portalUrl}</p>}
        <h2 className="font-semibold text-gray-900 mb-4">Bids</h2>
        {request.bids.length === 0 ? (
          <p className="text-gray-500">No bids yet.</p>
        ) : (
          <ul className="space-y-4">
            {request.bids.map((b) => (
              <li key={b.id} className="bg-white p-4 rounded-lg border border-gray-100">
                <p className="font-medium">{b.contractor?.company ?? 'Contractor'}</p>
                <p className="text-gray-600">{b.amountCents != null ? `$${(b.amountCents / 100).toFixed(2)}` : 'N/A'}</p>
                {b.scopeNotes && <p className="text-sm text-gray-500 mt-2">{b.scopeNotes}</p>}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};

export default RealtorQuoteDetail;
// CB:END fr-014-realtor-quote-detail
